import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { ClauseLibraryViewType } from '~/enums/ClauseLibraryViewType';
import { api } from '~/redux/api/createApi';
import { clauseLibraryListClauses } from '~/redux/api/methods/clauseLibrary';
import { coerceRtkqError } from '~/redux/api/utils';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { htmlToText } from '~/utils/strings';

import {
  CLAUSE_LIBRARY_NAME_CHAR_LIMIT,
  GUIDANCE_NOTES_CHAR_LIMIT,
} from '../constants';
import { Position } from '../types';
import ClauseForm from './ClauseForm';
import VariationForm from './VariationForm';

const ClauseLibraryManagement = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { navigate } = useRouting();

  const clauseForm = useSelector(
    slices.clauseLibrary.selectors.selectClauseForm,
  );
  const variationForm = useSelector(
    slices.clauseLibrary.selectors.selectVariationForm,
  );
  const editMode = useSelector(slices.clauseLibrary.selectors.selectEditMode);
  const view = useSelector(slices.clauseLibrary.selectors.selectView);

  //// SERVER CALLS
  const [createClause, createClauseResult] =
    api.endpoints.clauseLibraryCreateClause.useMutation();
  const {
    error: createClauseError,
    isError: isCreateClauseError,
    isSuccess: isCreateClauseSuccess,
  } = createClauseResult;

  const [editClause, editClauseResult] =
    api.endpoints.clauseLibraryEditClause.useMutation();
  const {
    error: editClauseError,
    isError: isEditClauseError,
    isSuccess: isEditClauseSuccess,
  } = editClauseResult;

  const [createVariation, createVariationResult] =
    api.endpoints.clauseLibraryCreateVariation.useMutation();
  const {
    error: createVariationError,
    isError: isCreateVariationError,
    isSuccess: isCreateVariationSuccess,
  } = createVariationResult;

  const [editVariation, editVariationResult] =
    api.endpoints.clauseLibraryEditVariation.useMutation();
  const {
    error: editVariationError,
    isError: isEditVariationError,
    isSuccess: isEditVariationSuccess,
  } = editVariationResult;

  //// FORM VALIDATION
  const clauseName = clauseForm.name?.trim();
  const clauseNameMissing = !clauseName;
  const clauseNameOverLimit =
    (clauseName?.length || 0) > CLAUSE_LIBRARY_NAME_CHAR_LIMIT;
  const clauseNameInvalid = clauseNameMissing || clauseNameOverLimit;
  const clauseGuidanceNotes = clauseForm.guidanceNotes;
  const clauseGuidanceNotesOverLimit =
    htmlToText(clauseGuidanceNotes || '').trim().length >
    GUIDANCE_NOTES_CHAR_LIMIT;
  const clauseFormInvalid = clauseNameInvalid || clauseGuidanceNotesOverLimit;

  const variationName = variationForm.name?.trim();
  const variationNameOverLimit =
    (variationName?.length || 0) > CLAUSE_LIBRARY_NAME_CHAR_LIMIT;
  const variationPositionMissing = !variationForm?.position;
  const variationGuidanceNotes = variationForm.guidanceNotes;
  const variationGuidanceNotesMissing = !variationGuidanceNotes;
  const variationGuidanceNotesOverLimit =
    htmlToText(variationGuidanceNotes || '').trim().length >
    GUIDANCE_NOTES_CHAR_LIMIT;
  const variationText = variationForm.clauseText;
  const variationTextMissing = !variationText;
  const variationTextOverLimit =
    htmlToText(variationText || '').trim().length > GUIDANCE_NOTES_CHAR_LIMIT;
  const variationFormInvalid =
    variationNameOverLimit ||
    variationPositionMissing ||
    variationGuidanceNotesOverLimit ||
    variationTextOverLimit ||
    (variationGuidanceNotesMissing && variationTextMissing);

  const saveText = intl.formatMessage({
    id: 'ClauseLibraryManagement.saveChangesButton',
    defaultMessage: 'Save Changes',
    description: 'Save changes button text',
  });

  //// POST-SUBMIT, DISPLAY TOAST, & ERROR HANDLING
  const postCreateClause = async () => {
    // selectedClause relies on id in url which is not available on creation
    const response = await clauseLibraryListClauses({});
    const clause = response.find((clause) => clause.name === clauseForm.name);
    dispatch(
      slices.clauseLibrary.actions.resetClauseLibraryExceptSelectedClause(),
    );
    navigate(`/clause-library/${clause?.key || ''}`);
  };

  const postEditClause = () => {
    dispatch(
      slices.clauseLibrary.actions.resetClauseLibraryExceptSelectedClause(),
    );
    navigate(`/clause-library/${clauseForm.clauseId}`);
  };

  const postCreateVariation = async () => {
    // selectedClause relies on id in url which is not available on creation
    const response = await clauseLibraryListClauses({});
    const clause = response.find(
      (clause) => clause.key === variationForm.clauseId,
    );
    const variation = clause?.children.find(
      (variation) =>
        variation.name ===
        (variationForm.name ?? variationForm.namePlaceholder),
    );
    dispatch(
      slices.clauseLibrary.actions.resetClauseLibraryExceptSelectedClause(),
    );
    navigate(`/clause-library/${clause?.key ?? ''}#${variation?.key ?? ''}`);
  };

  const postEditVariation = () => {
    dispatch(
      slices.clauseLibrary.actions.resetClauseLibraryExceptSelectedClause(),
    );
    navigate(
      `/clause-library/${variationForm.clauseId}#${variationForm.variationId}`,
    );
  };

  useEffect(() => {
    if (isCreateClauseSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'ClauseLibraryManagement.createClauseSuccess',
            defaultMessage: 'Successfully added {clauseName}',
            description:
              'Success message when clause is created. Example: clauseName = "Termination Clause"',
          },
          { clauseName },
        ),
      );
      postCreateClause();
    }
    if (isCreateClauseError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'ClauseLibraryManagement.createClauseError',
            defaultMessage: 'Error occurred while creating {clauseName}',
            description:
              'Error message when clause creation fails. Example: clauseName = "Termination Clause"',
          },
          { clauseName },
        ),
      );
      if (
        !!coerceRtkqError(createClauseError)?.response?.data?.errors?.length
      ) {
        dispatch(
          slices.clauseLibrary.actions.setClauseForm({
            ...clauseForm,
            errors: coerceRtkqError(createClauseError).response.data.errors,
          }),
        );
      }
    }
  }, [isCreateClauseSuccess, isCreateClauseError]);

  useEffect(() => {
    if (isEditClauseSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'ClauseLibraryManagement.editClauseSuccess',
            defaultMessage: 'Changes to {clauseName} saved successfully',
            description:
              'Success message when clause is edited. Example: clauseName = "Termination Clause"',
          },
          { clauseName },
        ),
      );
      postEditClause();
    }
    if (isEditClauseError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'ClauseLibraryManagement.editClauseError',
            defaultMessage:
              'Error occurred while saving changes to {clauseName}',
            description:
              'Error message when clause edit fails. Example: clauseName = "Termination Clause"',
          },
          { clauseName },
        ),
      );
      if (!!coerceRtkqError(editClauseError)?.response?.data?.errors?.length) {
        dispatch(
          slices.clauseLibrary.actions.setClauseForm({
            ...clauseForm,
            errors: coerceRtkqError(editClauseError).response.data.errors,
          }),
        );
      }
    }
  }, [isEditClauseSuccess, isEditClauseError]);

  useEffect(() => {
    if (isCreateVariationSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'ClauseLibraryManagement.createVariationSuccess',
            defaultMessage: 'Successfully added: {variationName}',
            description:
              'Success message when variation is created. Example: variationName = "Variation A"',
          },
          { variationName },
        ),
      );
      postCreateVariation();
    }
    if (isCreateVariationError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'ClauseLibraryManagement.createVariationError',
            defaultMessage: 'Error occurred while creating {variationName}',
            description:
              'Error message when variation creation fails. Example: variationName = "Variation A"',
          },
          { variationName },
        ),
      );
      if (
        !!coerceRtkqError(createVariationError)?.response?.data?.errors?.length
      ) {
        dispatch(
          slices.clauseLibrary.actions.setVariationForm({
            ...variationForm,
            errors: coerceRtkqError(createVariationError).response.data.errors,
          }),
        );
      }
    }
  }, [isCreateVariationSuccess, isCreateVariationError]);

  useEffect(() => {
    if (isEditVariationSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'ClauseLibraryManagement.editVariationSuccess',
            defaultMessage: 'Changes to {variationName} saved successfully',
            description:
              'Success message when variation is edited. Example: variationName = "Variation A"',
          },
          { variationName },
        ),
      );
      postEditVariation();
    }
    if (isEditVariationError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'ClauseLibraryManagement.editVariationError',
            defaultMessage:
              'Error occurred while saving changes to {variationName}',
            description:
              'Error message when variation edit fails. Example: variationName = "Variation A"',
          },
          { variationName },
        ),
      );
      if (
        !!coerceRtkqError(editVariationError)?.response?.data?.errors?.length
      ) {
        dispatch(
          slices.clauseLibrary.actions.setVariationForm({
            ...variationForm,
            errors: coerceRtkqError(editVariationError).response.data.errors,
          }),
        );
      }
    }
  }, [isEditVariationSuccess, isEditVariationError]);

  //// VIEW LOGIC
  function handleClauseFormSubmission() {
    if (editMode) {
      editClause({
        clauseId: clauseForm.clauseId as string,
        name: clauseName,
        guidance: clauseGuidanceNotes,
      });
    } else {
      createClause({ name: clauseName, guidance: clauseGuidanceNotes });
    }
  }

  function handleVariationFormSubmission() {
    if (editMode) {
      editVariation({
        variationId: variationForm.variationId ?? '',
        nickname: variationName || (variationForm.namePlaceholder ?? ''),
        guidance: variationGuidanceNotes,
        text: variationText,
        position: variationForm.position as Position,
        requiresApproval: false,
      });
    } else {
      createVariation({
        clauseId: variationForm.clauseId ?? '',
        nickname: variationName || (variationForm.namePlaceholder ?? ''),
        guidance: variationGuidanceNotes,
        text: variationText,
        position: variationForm.position as Position,
        requiresApproval: false,
      });
    }
  }

  function displayClauseErrors() {
    const validationErrors = [];
    if (clauseNameMissing) validationErrors.push('clause name missing');
    dispatch(
      slices.clauseLibrary.actions.setClauseForm({
        ...clauseForm,
        validationErrors,
      }),
    );
  }

  function displayVariationErrors() {
    const validationErrors = [];
    if (variationPositionMissing) validationErrors.push('position missing');
    if (variationTextMissing) validationErrors.push('text missing');
    dispatch(
      slices.clauseLibrary.actions.setVariationForm({
        ...variationForm,
        validationErrors,
      }),
    );
  }

  const content = {
    [ClauseLibraryViewType.ClauseForm]: {
      component: ClauseForm,
      title: editMode
        ? intl.formatMessage(
            {
              id: 'ClauseLibraryManagement.editClauseTitle',
              defaultMessage: 'Edit {title}',
              description:
                'Title for edit clause modal. Example: title = "Termination Clause"',
            },
            { title: clauseForm.title },
          )
        : intl.formatMessage({
            id: 'ClauseLibraryManagement.newClauseTitle',
            defaultMessage: 'New Clause',
            description: 'Title for new clause modal',
          }),
      primary: {
        text: editMode
          ? saveText
          : intl.formatMessage({
              id: 'ClauseLibraryManagement.addClauseButton',
              defaultMessage: 'Add Clause',
              description: 'Add clause button text',
            }),
        onClick: clauseFormInvalid
          ? displayClauseErrors
          : handleClauseFormSubmission,
      },
      onCancel: () => {
        dispatch(
          slices.clauseLibrary.actions.setView(ClauseLibraryViewType.MainPage),
        );
        dispatch(slices.clauseLibrary.actions.setEditMode(false));
        dispatch(
          slices.clauseLibrary.actions.setClauseForm({
            name: '',
            guidanceNotes: '',
            title: '',
            errors: [],
            validationErrors: [],
          }),
        );
      },
    },
    [ClauseLibraryViewType.VariationForm]: {
      component: VariationForm,
      title: editMode
        ? intl.formatMessage(
            {
              id: 'ClauseLibraryManagement.editVariationTitle',
              defaultMessage: 'Edit {title}',
              description:
                'Title for edit variation modal. Example: title = "Variation A"',
            },
            { title: variationForm.title },
          )
        : intl.formatMessage({
            id: 'ClauseLibraryManagement.newVariationTitle',
            defaultMessage: 'New Clause Variation',
            description: 'Title for new variation modal',
          }),
      primary: {
        text: editMode
          ? saveText
          : intl.formatMessage({
              id: 'ClauseLibraryManagement.addVariationButton',
              defaultMessage: 'Add Clause Variation',
              description: 'Add variation button text',
            }),
        onClick: variationFormInvalid
          ? displayVariationErrors
          : handleVariationFormSubmission,
      },
      onCancel: () => {
        dispatch(
          slices.clauseLibrary.actions.setView(ClauseLibraryViewType.MainPage),
        );
        dispatch(slices.clauseLibrary.actions.setEditMode(false));
        dispatch(
          slices.clauseLibrary.actions.setVariationForm({
            name: '',
            guidanceNotes: '',
            clauseText: '',
            title: '',
            validationErrors: [],
          }),
        );
      },
    },
  };

  if (view === ClauseLibraryViewType.MainPage) return null;

  //   @ts-ignore
  const { component: Content, title, primary, onCancel } = content[view];
  return (
    <Modal
      title={title}
      isFullPage={true}
      // @ts-ignore
      isVisible={!!content[view]}
      primaryAction={primary}
      // secondaryActions={}
      onCancel={onCancel}
      cancelText={intl.formatMessage({
        id: 'ClauseLibraryManagement.cancelButton',
        defaultMessage: 'Cancel',
        description: 'Cancel button text',
      })}
    >
      <Layout w="80%" maxW="1000px" p={4} alignSelf="center" justify="center">
        <Content />
      </Layout>
    </Modal>
  );
};

export default ClauseLibraryManagement;
